var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import cx from 'clsx';
import { AnimatePresence, m as motion } from 'framer-motion';
import { formElementWrapperStyles } from '../../css/formElement.css';
import { getBorderStyles } from '../../utils/formElements';
import { Inline } from '../inline/Inline';
import { Stack } from '../stack/Stack';
import { InputLabel } from '../input_label/InputLabel';
import { ValidationMessage } from '../validation_message/ValidationMessage';
import { descriptionStyles, groupStyles, iconVariants, inputPaddingVariants, labelStyles, inputVariants, } from './Input.css';
const renderIcon = (iconComponent, type) => React.cloneElement(iconComponent, {
    'aria-hidden': true,
    className: cx(iconVariants[type], iconComponent.props.className),
    'data-testid': 'input-icon',
});
const InputComponent = React.forwardRef(function Input(_a, ref) {
    var { className, descriptionId, errorId, hasError = false, hasWarning = false, iconPrefix, iconSuffix, type = 'text', isDisabled = false, isReadOnly = false } = _a, rest = __rest(_a, ["className", "descriptionId", "errorId", "hasError", "hasWarning", "iconPrefix", "iconSuffix", "type", "isDisabled", "isReadOnly"]);
    return (_jsxs("div", { className: cx(formElementWrapperStyles, className), children: [iconPrefix && renderIcon(iconPrefix, 'prefix'), _jsx("input", Object.assign({}, rest, { "aria-describedby": descriptionId, "aria-errormessage": errorId, "aria-invalid": hasError, className: cx(inputVariants[isDisabled || isReadOnly ? 'frozen' : 'default'], getBorderStyles(isDisabled || isReadOnly, hasError, hasWarning), iconPrefix && inputPaddingVariants.withPrefixedIcon, iconSuffix && inputPaddingVariants.withSuffixedIcon), disabled: isDisabled, readOnly: isReadOnly, type: type, ref: ref })), iconSuffix && renderIcon(iconSuffix, 'suffix')] }));
});
const InputLabelComponent = React.forwardRef(function InputLabelComponent(_a, ref) {
    var { children, className } = _a, rest = __rest(_a, ["children", "className"]);
    return (_jsx(InputLabel, Object.assign({ className: cx(labelStyles, className) }, rest, { ref: ref, children: children })));
});
const InputGroup = React.forwardRef(function InputGroup(_a, ref) {
    var { children } = _a, rest = __rest(_a, ["children"]);
    return (_jsx(Inline, Object.assign({ className: groupStyles, "data-inputgroup": true, noWrap: true, spacing: 0 }, rest, { ref: ref, children: children })));
});
const motionVariants = {
    parent: {
        enter: { height: 'auto', transition: { type: 'tween', duration: 0.2, when: 'beforeChildren' } },
        exit: { height: 0, transition: { type: 'tween', duration: 0.2 } },
    },
    child: {
        enter: { opacity: 1, transition: { duration: 0.2 } },
        exit: { opacity: 0, transition: { type: 'tween', duration: 0.15 } },
    },
};
const InputMeta = React.forwardRef(function InputMeta({ description, descriptionId, error, errorId, hasError, hasWarning, warning }, ref) {
    return (_jsxs(Stack, { "aria-live": "assertive", spacing: 4, children: [description && (_jsx("div", { className: cx(descriptionStyles, labelStyles), id: descriptionId, children: description })), _jsxs(AnimatePresence, { initial: false, children: [hasError && error && (_jsx(motion.div, { animate: "enter", exit: "exit", initial: "exit", ref: ref, variants: motionVariants.parent, children: _jsx(motion.div, { variants: motionVariants.child, children: _jsx(ValidationMessage, { id: errorId, variant: "error", children: error }) }, "error") })), !hasError && hasWarning && warning && (_jsx(motion.div, { animate: "enter", exit: "exit", initial: "exit", ref: ref, variants: motionVariants.parent, children: _jsx(motion.div, { variants: motionVariants.child, children: _jsx(ValidationMessage, { variant: "warning", children: warning }) }, "warning") }))] })] }));
});
export const Input = InputComponent;
Input.Group = InputGroup;
Input.Label = InputLabelComponent;
Input.Meta = InputMeta;
